import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';

import { routes } from '../../utils/routes';
import rukaLogo from './ruka-logo-ski-resort.png';
import pyhaLogo from './pyha-logo.svg';
import openMenuIcon from './icons/bars.svg';
import closeMenuIcon from './icons/icon-close.svg';
import internalLinkIcon from './icons/arrow-black-right.svg';
import externalLinkIcon from './icons/outside-link-icon.svg';
import logoutIcon from './icons/log-out-icon.svg';
import arrowDown from './icons/arrow-down.svg';
import arrowUp from './icons/arrow-up.svg';
import { getDomain, getDrupalLink } from '../../utils/functions';
import { getColorVars } from '../../utils/functions';
import { Customer } from '../../types/customer';

interface LanguageNames {
  fi: string;
  en: string;
}

interface Props {
  customer?: Customer;
}

function Navigation(props: Props): JSX.Element {
  const domain = getDomain();

  const [menuIsOpen, setMenuOpen] = useState(false);
  const [langMenuIsOpen, setLangMenuIsOpen] = useState(false);
  const { t, i18n } = useTranslation(['menu']);
  const { language } = i18n;
  const location = useLocation();
  const navigate = useNavigate();

  const logo: { [key: string]: string } = {
    ruka: rukaLogo,
    pyha: pyhaLogo,
  };

  const languageIndex: number = language === 'fi' ? 0 : 1;
  const accommodationSearchUrl: { [key: string]: string } = {};
  accommodationSearchUrl['ruka'] =
    process.env.REACT_APP_ACCOMMODATION_SEARCH_RUKA?.split(',')[
      languageIndex
    ] ?? '';
  accommodationSearchUrl['pyha'] =
    process.env.REACT_APP_ACCOMMODATION_SEARCH_PYHA?.split(',')[
      languageIndex
    ] ?? '';

  const { bgColor, textColor, hoverBgColor } = getColorVars();

  const { accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const isOwner = accounts[0]?.idTokenClaims?.jobTitle === 'owner';
  const isLoyaltyMember = props.customer
    ? props.customer.loyaltyMembershipStatus === 'Active'
    : false;

  const siteUrl: { [key: string]: string } = {
    ruka: process.env.REACT_APP_DRUPAL_BASE_URL_RUKA ?? 'https://www.ruka.fi',
    pyha: process.env.REACT_APP_DRUPAL_BASE_URL_PYHA ?? 'https://www.pyha.fi',
  };
  const siteUrlTitle: { [key: string]: string } = {
    ruka: 'Ruka.fi',
    pyha: 'Pyha.fi',
  };
  const urlDir: { [key: string]: string } = {
    en: 'skiresort',
    fi: 'hiihtokeskus',
  };

  const menuItems = [
    {
      title: t('Front page'),
      href: `/${i18n.language}`,
      classNames: 'text-black',
    },
    {
      title: t('My Ski-Inn reservations'),
      href: `/${language}/${routes.BOOKINGS[i18n.language]}`,
      classNames: 'text-black',
    },
    isLoyaltyMember && {
      title: t('Benefits'),
      href: `/${language}/${routes.BENEFITS[i18n.language]}`,
      classNames: 'text-black',
    },
    isOwner && {
      title: t('Quotas'),
      href: `/${language}/${routes.QUOTAS[i18n.language]}`,
      classNames: 'text-black',
    },
    isOwner && {
      title: t('Own apartment'),
      href: `/${language}/${routes.ROOMS[i18n.language]}`,
      classNames: 'text-black',
    },
    {
      title: t('Purchase history'),
      href: `/${language}/${routes.ORDER_HISTORY[i18n.language]}`,
      classNames: 'text-black',
    },
    {
      title: t('Ski-days'),
      href: `/${language}/${routes.SKIDAYS[i18n.language]}`,
      classNames: 'text-black',
    },
    isOwner && {
      title: 'Hausvise',
      href: routes.HAUSVISE[i18n.language],
      ext: true,
      icon: externalLinkIcon,
    },
    {
      title: t('Make a new Ski-Inn booking'),
      href: getDrupalLink(accommodationSearchUrl[domain]),
      ext: true,
      icon: externalLinkIcon,
    },
    {
      title: siteUrlTitle[domain],
      href: getDrupalLink(
        `${siteUrl[domain]}/${i18n.language}/${urlDir[i18n.language]}`
      ),
      ext: true,
      icon: externalLinkIcon,
    },
  ];

  const langNames: LanguageNames = {
    fi: 'Finnish',
    en: 'English',
  };
  const languages = process.env.REACT_APP_LANGUAGES?.split(',') ?? [];
  const languageSwitcherLinks = languages.map((lang: string) => {
    const route = Object.values(routes).find((route) =>
      location.pathname.includes(route[i18n.language])
    );

    const toUrl = route ? `/${lang}/${route[lang]}` : `/${lang}`;
    const langKey = lang as keyof LanguageNames;
    const langName = langNames[langKey];
    return (
      <li key={lang} className='block relative cursor-pointer'>
        <a
          onClick={() => changeLanguage(lang, toUrl)}
          className={`bg-white text-black ${hoverBgColor} hover:text-white no-underline pl-5 pr-10 py-3 block`}
        >
          <span className='uppercase'>{lang}</span> {t(langName)}
        </a>
      </li>
    );
  });

  const menuItemStyles = 'font-rukaNeue border-b border-black mx-4';
  const menuLinkStyles =
    'text-xl font-medium no-underline py-2 px-1 flex justify-between';

  const logOutButton = (
    <a
      className={`${menuLinkStyles} ${textColor} cursor-pointer`}
      onClick={logOut}
    >
      {t('Logout')}
      <img src={logoutIcon} alt={t('Link icon') ?? 'Link icon'} />
    </a>
  );

  function changeLanguage(lang: string, url: string) {
    i18n.changeLanguage(lang);
    setLangMenuIsOpen(false);
    navigate(url);
  }

  async function logOut() {
    // Log out from drupal.
    try {
      await fetch(`${siteUrl[domain]}/user/logout`, {
        credentials: 'include',
        mode: 'no-cors',
      });
    } catch (error) {
      console.log(error);
    }

    window.location.replace(
      `/logout?logout_destination=${window.location.origin}`
    );
  }

  function toggleMenu() {
    setMenuOpen(!menuIsOpen);
    if (langMenuIsOpen) {
      setLangMenuIsOpen(false);
    }
  }

  function toggleLangMenu() {
    setLangMenuIsOpen(!langMenuIsOpen);
    if (menuIsOpen) {
      setMenuOpen(false);
    }
  }

  return (
    <div className='header'>
      <div
        className={[
          'navigation',
          `${bgColor}`,
          'w-full',
          'h-16',
          'text-white',
          'flex',
          'md:relative',
          'z-20',
        ].join(' ')}
      >
        <a href={`/${i18n.language}`}>
          <img className='h-full' src={logo[domain]} alt='Logo' />
        </a>

        <div
          className={`cursor-pointer text-white uppercase ml-auto ${
            !isAuthenticated ? 'mr-5' : ''
          } font-bold flex items-center md:absolute md:right-40 md:h-full`}
          onClick={toggleLangMenu}
        >
          {language}
          <img
            src={langMenuIsOpen ? arrowUp : arrowDown}
            alt={
              langMenuIsOpen
                ? t('Language menu is open') ?? ''
                : t('Language menu is closed') ?? ''
            }
            className='ml-4'
          />
        </div>

        {/* Menu is shown only to the authenticate users. */}
        {isAuthenticated && (
          <div
            onClick={toggleMenu}
            className='menu-icon h-full flex items-center px-10 md:absolute md:right-8 cursor-pointer'
          >
            <img
              src={menuIsOpen ? closeMenuIcon : openMenuIcon}
              className='h-1/3'
              alt={menuIsOpen ? 'Menu is open' : 'Menu is closed'}
            />
          </div>
        )}
      </div>
      {langMenuIsOpen && (
        <ul className='language-switcher absolute right-7 z-50 shadow-md border-3 border-solid border-transparent block md:right-20'>
          {languageSwitcherLinks}
        </ul>
      )}
      {menuIsOpen && (
        <div className='menu absolute bg-white w-full h-screen z-50'>
          <ul className='menu-items mt-2'>
            {menuItems.map(
              (menuItem, i) =>
                menuItem && (
                  <li className={menuItemStyles} key={i}>
                    <a
                      href={menuItem.href}
                      target={menuItem.ext ? '_blank' : '_self'}
                      rel='noreferrer'
                      className={`${menuLinkStyles} text-black`}
                    >
                      {menuItem.title}
                      <img
                        src={menuItem.icon ?? internalLinkIcon}
                        alt={t('Link icon') ?? 'Link icon'}
                      />
                    </a>
                  </li>
                )
            )}
            <li className={menuItemStyles}>{logOutButton}</li>
          </ul>
        </div>
      )}
    </div>
  );
}

export default Navigation;
